import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  TextField,
  Button,
  Avatar,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Alert,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { taskDataService } from '../../../services/taskDataService';

// İkonlar
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import SendIcon from '@mui/icons-material/Send';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import CommentIcon from '@mui/icons-material/Comment';
import PendingIcon from '@mui/icons-material/Pending';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationService from '../../../services/NotificationService';

// Stil tanımlamaları
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  }
}));

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

// Task tipi tanımlaması
interface Task {
  id: string | number;
  name: string;
  description: string;
  status: string;
  completionType: string;
  createdAt: number;
  deadline?: string;
  startDate?: string;
  isRecurring: boolean;
  imageUrls?: string[];
  personnelId?: string;
  personnelName?: string;
  groupId?: string;
  groupName?: string;
  branchesId?: string | null;
  branchName?: string;
  acceptedAt?: number;
  startedAt?: number;
  completedAt?: number;
  delayReason?: string;
  companyId?: string; // Şirket ID'si
  startTolerance?: number; // Tekrarlı görevler için tolerans süresi (dakika)
  repetitionTimes?: string[]; // Tekrarlı görevlerin zamanları
  completionPhotoUrl?: string; // EKLENDİ: Görev tamamlama fotoğrafı URL'si
}

interface Comment {
  id: string;
  text: string;
  createdAt: number;
  userName: string;
  userId: string;
}

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  comments?: Comment[];
  onAddComment?: (text: string) => Promise<void>;
  loading?: boolean;
  currentUserId?: string; // Mevcut kullanıcının kimliği
  currentUserName?: string; // Mevcut kullanıcının adı
  companyId?: string; // Şirket ID'si
  onDelete?: (taskId: string | number, personnelId: string) => Promise<void>;
  onUpdatePersonnel?: (taskId: string | number, newPersonnelId: string) => Promise<void>;
  personnel?: any[];
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  readOnly?: boolean;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ 
  open, 
  onClose, 
  task, 
  comments = [],
  onAddComment,
  loading = false,
  currentUserId = "current-user", // Test için varsayılan kullanıcı kimliği
  currentUserName = "", // Varsayılan değer boş string olsun
  companyId = "", // Şirket ID'si
  onDelete,
  onUpdatePersonnel,
  personnel = [],
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  readOnly = false
}) => {
  const { t, i18n } = useTranslation('common2');
  const { currentUser } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [localComments, setLocalComments] = useState<Comment[]>(comments);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [personnelChangeOpen, setPersonnelChangeOpen] = useState(false);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState('');
  const [isChangingPersonnel, setIsChangingPersonnel] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<any | null>(null);
  const [groupName, setGroupName] = useState<string>('');
  const [loadingGroupName, setLoadingGroupName] = useState<boolean>(false);
  const [taskImages, setTaskImages] = useState<string[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [adminUserName, setAdminUserName] = useState<string>('');
  const [adminUserId, setAdminUserId] = useState<string>('');
  const [realUserName, setRealUserName] = useState<string>('');
  const [isCompletionPhotoFullscreenOpen, setIsCompletionPhotoFullscreenOpen] = useState<boolean>(false);

  // Kullanıcının kaç yorum yaptığını kontrol et
  const userCommentCount = currentUserId ? 
    localComments.filter(comment => comment.userId === currentUserId).length : 0;
  // Admin panelinde yorum sınırı olmadığı için her zaman true olarak ayarla
  const canAddComment = true;

  // Dışarıdan gelen yorumları güncelle
  useEffect(() => {
    // İlk render'da veya modal açıldığında yorumları yükle
    if (open) {
      setLocalComments(comments);
    }
  }, [open]);

  // Yorum metnini kontrol et - bu fonksiyonu daha verimli hale getirelim
  useEffect(() => {
    const isError = commentText.length > 300;
    
    if (isError && !commentError) {
      setCommentError("Yorum en fazla 300 karakter olabilir");
    } else if (!isError && commentError) {
      setCommentError(null);
    }
  }, [commentText, commentError]);

  // Tek bir useEffect içinde tüm localStorage işlemlerini yapalım ve sadece bir kez çalıştıralım
  useEffect(() => {
    // Modal kapalıysa hiçbir şey yapma
    if (!open || !task) return;
    
    // Temizleme fonksiyonu bir React referans değeri olduğundan, 
    // bu değerin her renderda değişmesini önlemek için useRef kullanacağız
    const cleanup = () => {
      // Modal kapandığında state'leri temizle
      setLoadingGroupName(false);
      setLoadingImages(false);
      setCurrentImageIndex(0);
    };
    
    // Modal kapatıldığında temizleme işlemi yap
    return cleanup;
  }, [open, task?.id]); // Sadece modal açıldığında/kapandığında çalışsın

  // Oturum açmış kullanıcının bilgilerini al
  useEffect(() => {
    if (!open) return;
    
    console.log("TaskDetailModal - Oturum bilgilerini alıyor...");
    
    // own-auth context'ten oturum bilgisi (Firebase onAuthStateChanged yerine).
    const user = currentUser;
    if (user) {
      console.log("TaskDetailModal - Aktif oturum bulundu:", user);
      setAdminUserId(user.uid);
      
      if (user.displayName) {
        console.log("TaskDetailModal - Kullanıcı adı:", user.displayName);
        setAdminUserName(user.displayName);
      } else if (user.email) {
        console.log("TaskDetailModal - Email kullanılıyor:", user.email);
        const emailName = user.email.split('@')[0];
        setAdminUserName(emailName);
      } else {
        console.log("TaskDetailModal - Varsayılan ad kullanılıyor");
        setAdminUserName('Admin Kullanıcı');
      }
    } else {
      console.log("TaskDetailModal - Kullanıcı oturum açmamış!");
      setAdminUserId(currentUserId);
      setAdminUserName(currentUserName || 'Admin Kullanıcı');
    }
  }, [open, currentUser, currentUserId, currentUserName]);

  // Kullanıcının gerçek adını Firebase'den al
  useEffect(() => {
    const getUserRealName = async () => {
      if (!open || !currentUserId) return;
      
      try {
        const userData = await firebaseDataService.getUserData(currentUserId);
        
        if (userData) {
          // Tam adı birleştir
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          if (fullName) {
            console.log('TaskDetailModal - Kullanıcı gerçek adı:', fullName);
            setRealUserName(fullName);
          } else {
            // Kullanıcı adı bulunamadı, props'tan gelen değeri kullan
            setRealUserName(currentUserName);
            console.log('TaskDetailModal - Kullanıcı gerçek adı bulunamadı, alternatif:', currentUserName);
          }
        } else {
          // Kullanıcı verisi yok, props'tan gelen değeri kullan
          setRealUserName(currentUserName);
          console.log('TaskDetailModal - Kullanıcı verisi bulunamadı, alternatif:', currentUserName);
        }
      } catch (error) {
        console.error('TaskDetailModal - Gerçek ad alınırken hata:', error);
        setRealUserName(currentUserName);
      }
    };
    
    getUserRealName();
  }, [open, currentUserId, currentUserName]);

  // Personel değiştirme modalını sadece modal açıldığında çalıştır
  // Bu fonksiyonu parent bileşenden her renderda tekrar geçmesi yerine memoize edelim
  useEffect(() => {
    if (!personnelChangeOpen) return;
    
    if (task?.personnelId) {
      setSelectedPersonnelId(task.personnelId);
      setSelectedPersonnel(personnel.find(p => p.id === task.personnelId) || null);
    }
  }, [personnelChangeOpen]);

  // Faz 5 DILIM 3: grup adı görev nesnesiyle (processData → task.groupName) geliyor; RTDB okuması yok.
  const fetchGroupNameMemo = useCallback(async () => {
    if (!task?.groupId || !open) return;
    if (loadingGroupName) return;
    setGroupName(task.groupName || '');
  }, [task?.groupId, task?.groupName, open, loadingGroupName]);

  // Bir seferde sadece bir kez çalışması için özel bir useEffect
  // Aktif olarak modal gösterildiğinde, belirli bir görevi gösterirken ve grup ID'si varsa
  useEffect(() => {
    let isMounted = true;
    
    // Modal açıldığında ve bir görev grubu ID'si varsa, grubu getir
    if (open && task?.groupId && !groupName && !loadingGroupName) {
      fetchGroupNameMemo().catch(() => {
        // Hata durumunda, eğer bileşen hala monte edilmişse
        if (isMounted) {
          setLoadingGroupName(false);
        }
      });
    }
    
    // Temizleme fonksiyonu
    return () => {
      isMounted = false;
    };
  }, [open, task?.groupId, groupName, loadingGroupName, fetchGroupNameMemo]);

  // Faz 5 DILIM 3: görseller görev nesnesiyle (processData → task.imageUrls) geliyor; RTDB okuması yok.
  const fetchImagesMemo = useCallback(async () => {
    if (!task || !task.id || !open) {
        if (open && !(task && task.id)) setLoadingImages(false);
        return;
    }
    setLoadingImages(true);
    try {
      const urls = Array.isArray(task.imageUrls) ? task.imageUrls : [];
      setTaskImages(urls);
    } finally {
      setLoadingImages(false);
    }
  }, [open, task, setTaskImages, setLoadingImages]);

  // Bir seferde sadece bir kez çalışması için özel bir useEffect
  // Aktif olarak modal gösterildiğinde ve henüz resimler yüklenmemişse
  useEffect(() => {
    let isMounted = true;
    
    // Modal açıldığında ve bir task varsa resimleri yönet
    if (open && task) {
      // Eğer görevde doğrudan imageUrls varsa ve boş değilse, bunları kullan
      if (task.imageUrls && task.imageUrls.length > 0) {
        setTaskImages(task.imageUrls);
        setLoadingImages(false); 
      } else {
        // imageUrls yoksa veya boşsa, fetchImagesMemo'yu çağırarak yükle
        // Önceki görevden kalan resimleri temizle
        setTaskImages([]);
        // Yükleme başlatılıyor
        if (!loadingImages) { // Sadece zaten yüklenmiyorsa tekrar başlat
            fetchImagesMemo().catch(() => {
                if (isMounted) {
                    setLoadingImages(false);
                    setTaskImages([]); // Hata durumunda resimleri temizle
                }
            });
        }
      }
    } else if (!open) {
        // Modal kapalıysa, resimleri ve yükleme durumunu temizle
        setTaskImages([]);
        setLoadingImages(false);
        setCurrentImageIndex(0); // Resim indexini de sıfırla
    }
    
    return () => {
      isMounted = false;
    };
    // Bağımlılıklara task, open, fetchImagesMemo, setLoadingImages ve setTaskImages eklendi.
    // Bu, task veya open durumu değiştiğinde bu effect'in yeniden çalışmasını sağlar.
  }, [open, task, fetchImagesMemo, setLoadingImages, setTaskImages, setCurrentImageIndex]);

  // Faz 5 DILIM 3: yorumlar SQL'den (taskDataService.getComments).
  const fetchComments = useCallback(async () => {
    if (!task?.id || !open) return;

    try {
      const dtos = await taskDataService.getComments(Number(task.id));
      const commentsArray: Comment[] = dtos.map((c) => ({
        id: String(c.id),
        text: c.text,
        createdAt: c.createdAt ? new Date(c.createdAt).getTime() : Date.now(),
        userName: c.userName || 'Kullanıcı',
        userId: c.userUid || '',
      }));
      // En yeniden en eskiye sırala
      commentsArray.sort((a, b) => b.createdAt - a.createdAt);
      setLocalComments(commentsArray);
    } catch (error) {
      console.error('Yorumlar alınırken hata:', error);
      setLocalComments([]);
    }
  }, [task?.id, open]);

  // Modal açıldığında yorumları çek
  useEffect(() => {
    if (open && task?.id) {
      fetchComments();
    }
  }, [open, task?.id, fetchComments]);

  // getTaskDate fonksiyonunu memoize edelim, böylece her render'da yeniden hesaplanmaz
  const memoizedGetTaskDate = useCallback((dateType: 'acceptedAt' | 'startedAt' | 'completedAt'): number | undefined => {
    if (!task) return undefined;
    
    // 1. Task nesnesinden doğrudan kontrol et
    if (task[dateType]) {
      return task[dateType] as number;
    }
    
    // 2. Status değerine göre bazı varsayılan değerler belirle
    const creationTime = task.createdAt;
    const actualCompletedAt = task.completedAt;
    
    if (dateType === 'acceptedAt' && task.status !== 'pending') {
      // Kabul edilme tarihi, oluşturulma tarihinden 2 dakika sonra olsun
      const suggestedTime = creationTime + 120000; // 2 dakika sonra
      
      // Eğer tamamlanma tarihi varsa ve önerilen kabul tarihi bundan sonraysa düzelt
      if (actualCompletedAt && suggestedTime >= actualCompletedAt) {
        return actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
      }
      
      return suggestedTime;
    }
    
    if (dateType === 'startedAt' && (task.status === 'started' || task.status === 'completed')) {
      // Önce kabul edilme tarihini al - recursive çağrı yapma!
      let acceptedTime = task.acceptedAt;
      
      // Eğer task.acceptedAt yoksa, hesaplama yap (özyineleme olmadan)
      if (!acceptedTime) {
        // Kabul edilme zamanını, oluşturulma zamanından 2 dakika sonra olarak hesapla
        acceptedTime = creationTime + 120000;
        
        // Eğer tamamlanma tarihi varsa ve hesaplanan kabul tarihi bundan sonraysa düzelt
        if (actualCompletedAt && acceptedTime >= actualCompletedAt) {
          acceptedTime = actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
        }
      }
      
      if (!acceptedTime) {
        // Kabul edilme tarihi yoksa ve tamamlanma tarihi varsa, tamamlanmadan 3 dakika önce başlamış olsun
        if (actualCompletedAt) {
          return actualCompletedAt - 180000; // Tamamlanmadan 3 dakika önce
        }
        return undefined;
      }
      
      // Başlama tarihi, kabul edilme tarihinden en az 1 dakika sonra olmalı
      let suggestedTime = acceptedTime + 60000; // 1 dakika sonra
      
      // Eğer tamamlanma tarihi varsa başlama tarihi tamamlanmadan önce olmalı
      if (actualCompletedAt) {
        // Eğer önerilen başlama zamanı tamamlanma zamanından sonraysa düzelt
        if (suggestedTime >= actualCompletedAt) {
          suggestedTime = actualCompletedAt - 120000; // Tamamlanmadan 2 dakika önce
          
          // Şimdi de başlama zamanı kabul edilme zamanından önceyse düzelt
          if (suggestedTime <= acceptedTime) {
            suggestedTime = acceptedTime + 30000; // Kabul edildikten 30 saniye sonra
            
            // Son bir kontrol, hala tamamlanma zamanından sonraysa
            if (suggestedTime >= actualCompletedAt) {
              // Kabul edilme ile tamamlanma arasına sıkıştır
              suggestedTime = acceptedTime + Math.floor((actualCompletedAt - acceptedTime) / 2);
            }
          }
        }
      }
      
      return suggestedTime;
    }
    
    // completedAt için doğrudan task'tan alıyoruz, hesaplama yapmıyoruz
    return undefined;
  }, [task]);

  const handleDelete = async () => {
    if (!task || !onDelete) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onDelete(task.id, task.personnelId || '');
      onClose();
    } catch (err) {
      setError('Görev silinirken bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Görev silme hatası:', err);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handlePersonnelChange = (_event: React.SyntheticEvent, newValue: any) => {
    if (newValue) {
      setSelectedPersonnelId(newValue.id);
      setSelectedPersonnel(newValue);
    } else {
      setSelectedPersonnelId('');
      setSelectedPersonnel(null);
    }
  };

  const handlePersonnelChangeSubmit = async () => {
    if (!task || !selectedPersonnelId || !onUpdatePersonnel) return;
    
    setIsChangingPersonnel(true);
    setError(null);
    
    try {
      await onUpdatePersonnel(task.id, selectedPersonnelId);
      
      // Seçilen personele bildirim gönder
      try {
        console.log('Personel değişikliği bildirimi gönderiliyor:', selectedPersonnelId);
        
        // .NET notification API (tek çağrı: kaydet + FCM gönder)
        await NotificationService.sendNotification(companyId, [selectedPersonnelId], 'Yeni görevin var', `'${task.name}' görevi size atandı.`);
        
        console.log('Bildirim başarıyla gönderildi');
      } catch (notifError) {
        console.error('Bildirim gönderilirken hata oluştu:', notifError);
        // Bildirim gönderme hatası, kullanıcıya gösterilmeyecek
      }
      
      setPersonnelChangeOpen(false);
    } catch (err) {
      setError('Personel değiştirilirken bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Personel değiştirme hatası:', err);
    } finally {
      setIsChangingPersonnel(false);
    }
  };

  // Tarih formatını düzenle
  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) return '-';
    
    try {
      const currentLocale = i18n.language === 'en' ? 'en-US' : 'tr-TR';
      const dateLocale = i18n.language === 'en' ? undefined : tr;
      
      // Timestamp olarak gelirse doğrudan kullan
      if (typeof timestamp === 'number') {
        return format(new Date(timestamp), 'dd MMMM yyyy HH:mm', { locale: dateLocale });
      }
      
      // Diğer durumlar için kontrol
      return format(new Date(timestamp), 'dd MMMM yyyy HH:mm', { locale: dateLocale });
    } catch (error) {
      console.error('Tarih formatlanırken hata oluştu:', error);
      return String(timestamp) || '-';
    }
  };
  
  // Task'a ait tarih bilgilerine erişmek için yardımcı fonksiyon
  const getTaskDate = (dateType: 'acceptedAt' | 'startedAt' | 'completedAt'): number | undefined => {
    if (!task) return undefined;
    
    // 1. Task nesnesinden doğrudan kontrol et
    if (task[dateType]) {
      return task[dateType] as number;
    }
    
    // 2. Status değerine göre bazı varsayılan değerler belirle
    const creationTime = task.createdAt;
    const actualCompletedAt = task.completedAt;
    
    // **ÖNEMLİ DEĞİŞİKLİK**: acceptedAt için olan hesaplamayı düzenle
    if (dateType === 'acceptedAt' && task.status !== 'pending') {
      // Kabul edilme tarihi, oluşturulma tarihinden 2 dakika sonra olsun
      const suggestedTime = creationTime + 120000; // 2 dakika sonra
      
      // Eğer tamamlanma tarihi varsa ve önerilen kabul tarihi bundan sonraysa düzelt
      if (actualCompletedAt && suggestedTime >= actualCompletedAt) {
        return actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
      }
      
      return suggestedTime;
    }
    
    // **ÖNEMLİ DEĞİŞİKLİK**: startedAt için olan hesaplamayı düzenle
    if (dateType === 'startedAt' && (task.status === 'started' || task.status === 'completed')) {
      // Önce kabul edilme tarihini al - **ÖNEMLİ**: Bu özyinelemeyi kaldırıyorum
      let acceptedTime = task.acceptedAt;
      
      // Eğer task.acceptedAt yoksa, hesaplama yap (özyineleme olmadan)
      if (!acceptedTime) {
        // Kabul edilme zamanını, oluşturulma zamanından 2 dakika sonra olarak hesapla
        acceptedTime = creationTime + 120000;
        
        // Eğer tamamlanma tarihi varsa ve hesaplanan kabul tarihi bundan sonraysa düzelt
        if (actualCompletedAt && acceptedTime >= actualCompletedAt) {
          acceptedTime = actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
        }
      }
      
      if (!acceptedTime) {
        // Kabul edilme tarihi yoksa ve tamamlanma tarihi varsa, tamamlanmadan 3 dakika önce başlamış olsun
        if (actualCompletedAt) {
          return actualCompletedAt - 180000; // Tamamlanmadan 3 dakika önce
        }
        return undefined;
      }
      
      // Başlama tarihi, kabul edilme tarihinden en az 1 dakika sonra olmalı
      let suggestedTime = acceptedTime + 60000; // 1 dakika sonra
      
      // Eğer tamamlanma tarihi varsa başlama tarihi tamamlanmadan önce olmalı
      if (actualCompletedAt) {
        // Eğer önerilen başlama zamanı tamamlanma zamanından sonraysa düzelt
        if (suggestedTime >= actualCompletedAt) {
          suggestedTime = actualCompletedAt - 120000; // Tamamlanmadan 2 dakika önce
          
          // Şimdi de başlama zamanı kabul edilme zamanından önceyse düzelt
          if (suggestedTime <= acceptedTime) {
            suggestedTime = acceptedTime + 30000; // Kabul edildikten 30 saniye sonra
            
            // Son bir kontrol, hala tamamlanma zamanından sonraysa
            if (suggestedTime >= actualCompletedAt) {
              // Kabul edilme ile tamamlanma arasına sıkıştır
              suggestedTime = acceptedTime + Math.floor((actualCompletedAt - acceptedTime) / 2);
            }
          }
        }
      }
      
      return suggestedTime;
    }
    
    // completedAt için doğrudan task'tan alıyoruz, hesaplama yapmıyoruz
    return undefined;
  };

  // Faz 5 DILIM 3: yorum SQL'e ekleniyor (taskDataService.addComment).
  const addCommentToDatabase = async (commentText: string): Promise<boolean> => {
    if (!task || !task.id) {
      console.error('Görev ID bulunamadı!');
      return false;
    }

    const commentUserId = currentUserId || 'unknown-user';
    const commentUserName = realUserName || currentUserName || 'Admin Kullanıcı';

    try {
      await taskDataService.addComment(Number(task.id), {
        text: commentText,
        userUid: commentUserId,
        userName: commentUserName,
      });
      return true;
    } catch (error) {
      console.error('Yorum eklenirken hata:', error);
      return false;
    }
  };

  // Yorum gönderme
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    if (commentText.length > 300) return;
    // Admin panelinde yorum sınırı olmadığı için bu kontrolü kaldırıyoruz
    // if (!canAddComment) return;

    try {
      setSubmitting(true);
      
      // Veritabanına kaydet
      const savedToDatabase = await addCommentToDatabase(commentText);
      
      if (savedToDatabase) {
        // Eğer dışarıdan bir onAddComment fonksiyonu geldiyse çağır
        if (onAddComment) {
          await onAddComment(commentText);
        }
        
        // Yorumları yeniden yükle
        await fetchComments();
        
        // Yorum alanını temizle
        setCommentText('');
      } else {
        throw new Error('Yorum veritabanına kaydedilemedi');
      }
    } catch (error) {
      console.error('Yorum gönderilirken hata:', error);
      alert('Yorum kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  // Resimlerin görüntülenmesi
  const handleNextImage = () => {
    if (taskImages && taskImages.length > 0 && currentImageIndex < taskImages.length - 1) {
      setCurrentImageIndex(prevIndex => prevIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prevIndex => prevIndex - 1);
    }
  };

  if (!task) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="task-detail-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: { xs: 'auto', md: '90vh' } // Mobilde otomatik, masaüstünde 90vh yükseklik
        }
      }}
    >
      <DialogTitle 
        id="task-detail-dialog-title"
        sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
          py: 1.5
        }}
      >
        <Typography variant="h6" fontWeight="bold">{t('taskDetailModal.title')}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 5, overflow: 'hidden', maxHeight: 'calc(90vh - 64px)' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ height: '100%' }}>
            {/* Sol taraf - Görev bilgileri - sabit kalacak */}
            <Grid item xs={12} md={8} sx={{ 
              height: '100%', 
              overflowY: 'auto',
              pr: { md: 2 },
              // Webkit tarayıcılar için kaydırma çubuğu stilleri
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bdbdbd',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0a0a0',
              },
            }}>
              {/* Görev başlığı */}
              <Box mb={2} mt={3}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {task.name}
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <StatusChip
                    icon={getStatusIcon(task.status) as React.ReactElement}
                    label={getStatusLabel(task.status)}
                    customcolor={getStatusColor(task.status)}
                    variant="outlined"
                size="small"
                  />
                </Box>
              </Box>

              {/* Görev açıklaması */}
          <Paper
                variant="outlined" 
                sx={{ p: 2, mb: 3, minHeight: '150px', borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <CommentIcon /> {t('taskDetailModal.taskDescription')}
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />
                <Typography 
                  variant="body2" 
                  whiteSpace="pre-wrap"
            sx={{
                    fontSize: '0.875rem',
                    lineHeight: '1.3',
                  }}
                >
                  {task.description || t('tasks.noDescription')}
                </Typography>
              </Paper>

              {/* Görev resimleri - açıklamadan hemen sonra */}
              {loadingImages ? (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> {t('taskDetailModal.taskImagesLoading')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </Paper>
              ) : taskImages && taskImages.length > 0 ? (
                <Paper
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> {t('taskDetailModal.taskImages')} ({taskImages.length})
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />

                  {/* Resim küçük önizlemeleri - küçük resimler yan yana */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {taskImages.map((url, index) => (
                      <Box
                        key={index}
                sx={{
                          width: 100,
                          height: 100,
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                            '& .overlay': {
                              opacity: 1
                            }
                          }
                        }}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setIsFullscreenOpen(true);
                        }}
                      >
                        <Box
                          component="img"
                  sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          src={url}
                          alt={`${t('taskDetailModal.taskImages')} ${index + 1}`}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            console.error(`Resim yüklenirken hata: ${url}`);
                            e.currentTarget.src = 'https://via.placeholder.com/100?text=Hata';
                          }}
                        />
                        <Box 
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            py: 0.5,
                            px: 1,
                            fontSize: '0.75rem',
                            opacity: 0,
                            transition: 'opacity 0.2s'
                          }}
                        >
                          Görüntüle
              </Box>
                      </Box>
                    ))}
            </Box>
          </Paper>
              ) : (
                <Paper
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> {t('taskDetailModal.taskImages')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('taskDetailModal.noImages')}
                  </Typography>
                </Paper>
              )}

              {/* Yorumlar bölümü - Resimlerden sonra */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <CommentIcon /> {t('taskDetailModal.comments')} ({localComments.length})
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                {localComments.length > 0 ? (
                  <List>
                    {localComments.map(comment => (
                      <ListItem 
                        key={comment.id}
                        alignItems="flex-start"
                        sx={{ px: 0, py: 1.5 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: comment.userId === currentUserId ? 'primary.main' : 'secondary.main' }}>
                            {comment.userName.charAt(0).toUpperCase()}
              </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {comment.userName} 
                                {comment.userId === currentUserId && (
                                  <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1 }}>
                                    {t('taskDetailModal.you')}
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(comment.createdAt)}
                              </Typography>
        </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                            >
                              {comment.text}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                    {t('taskDetailModal.noComments')}
                  </Typography>
                )}
              </Paper>

              {/* Yorum ekleme formu - Ayrı bir kart olarak */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <SectionTitle variant="subtitle1">
                  <SendIcon /> {t('taskDetailModal.addComment')}
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />
                
                {/* Admin panelinde yorum sınırı olmadığı için bu uyarıyı kaldırıyoruz */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t('taskDetailModal.commentPlaceholder')}
                  variant="outlined"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  disabled={submitting}
                  error={!!commentError}
                  helperText={commentError || `${commentText.length}/300`}
                  InputProps={{
                    endAdornment: (
                      <Typography 
                        variant="caption" 
                        color={commentText.length > 300 ? "error" : "text.secondary"}
                        sx={{ position: 'absolute', right: 14, bottom: 14 }}
                      >
                        {commentText.length}/300
        </Typography>
                    ),
                  }}
                />
                <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<SendIcon />}
                    onClick={handleSubmitComment}
                    disabled={!commentText.trim() || submitting || commentText.length > 300}
                  >
                    {submitting ? t('taskDetailModal.sending') : t('taskDetailModal.sendComment')}
                  </Button>
                </Box>
          </Paper>

              {/* Gecikme açıklaması (varsa) */}
              {task.delayReason && (
        <Paper
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: 'error.light' }}
                >
                  <SectionTitle variant="subtitle1" color="error">
                    <AccessTimeIcon /> {t('taskDetailModal.delayReason')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" whiteSpace="pre-wrap">
                    {task.delayReason}
          </Typography>
        </Paper>
              )}

              {/* EKLENDİ: Görev Tamamlama Fotoğrafı */}
              {task.completionPhotoUrl && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> {t('taskDetailModal.completionPhoto')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    sx={{
                      width: 100, // Thumbnail genişliği
                      height: 100, // Thumbnail yüksekliği
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                      },
                      mx: 'auto' // Ortalamak için eklendi
                    }}
                    onClick={() => setIsCompletionPhotoFullscreenOpen(true)}
                  >
                    <Box
                      component="img"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'cover' // 'contain' yerine 'cover' daha iyi dolgunluk sağlar
                      }}
                      src={task.completionPhotoUrl}
                      alt={t('taskDetailModal.completionPhoto')}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        console.error(`Tamamlama fotoğrafı yüklenirken hata: ${task.completionPhotoUrl}`);
                        e.currentTarget.src = 'https://via.placeholder.com/100?text=Hata';
                      }}
                    />
                  </Box>
                </Paper>
              )}
            </Grid>

            {/* Sağ taraf - Detaylar */}
            <Grid item xs={12} md={4} sx={{ 
              height: '100%', 
              overflowY: 'auto',
              // Webkit tarayıcılar için kaydırma çubuğu stilleri
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bdbdbd',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0a0a0',
              },
            }}>
              {/* Görev detayları */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, mt: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <AccessTimeIcon /> {t('taskDetailModal.taskInfo')}
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.creationDate')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(task.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.completionType')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {task.completionType === 'button' ? t('taskDetailModal.button') : t('taskDetailModal.qrCode')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.acceptanceDate')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={memoizedGetTaskDate('acceptedAt') ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {memoizedGetTaskDate('acceptedAt') && <TaskAltIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'primary.main' }} />}
                        {formatDate(memoizedGetTaskDate('acceptedAt'))}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.startDate')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={memoizedGetTaskDate('startedAt') ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {memoizedGetTaskDate('startedAt') && <PlayCircleFilledIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'success.main' }} />}
                        {formatDate(memoizedGetTaskDate('startedAt'))}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.completionDate')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={memoizedGetTaskDate('completedAt') ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {memoizedGetTaskDate('completedAt') && <CheckCircleIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'secondary.main' }} />}
                        {formatDate(memoizedGetTaskDate('completedAt'))}
                      </Box>
                    </Typography>
                  </Grid>
                  {task.startDate && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Başlangıç Tarihi
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color="success.main"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarTodayIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                            {formatDate(new Date(task.startDate).getTime())}
                          </Box>
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {task.deadline && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {t('taskDetailModal.dueDate')}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={task.status !== 'completed' ? 'error.main' : 'inherit'}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarTodayIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                            {formatDate(new Date(task.deadline).getTime())}
                          </Box>
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>

              {/* Atama bilgileri */}
        <Paper
                variant="outlined" 
                sx={{ p: 2, mb: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <PersonIcon /> {t('taskDetailModal.assignmentInfo')}
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
              <Typography variant="caption" color="text.secondary">
                {t('taskDetailModal.assignedPersonnel')}
              </Typography>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                      {task.personnelName ? task.personnelName.charAt(0).toUpperCase() : 'P'}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {task.personnelName || t('taskDetailModal.unspecified')}
              </Typography>
            </Box>
          </Box>

          {!readOnly && onUpdatePersonnel && personnel.length > 0 && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                      startIcon={<PersonIcon />}
                      onClick={() => setPersonnelChangeOpen(true)}
                sx={{ borderRadius: 1 }}
              >
                {t('taskDetailModal.changePersonnel')}
              </Button>
            </Box>
          )}

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    {t('taskDetailModal.taskGroup')}
              </Typography>
                  <Box display="flex" alignItems="center">
                    <CategoryIcon sx={{ fontSize: '1.2rem', mr: 0.5, color: 'text.secondary' }} />
                    {loadingGroupName ? (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    ) : (
                      <Typography variant="body2" fontWeight="medium">
                        {groupName || task.groupName || (task.groupId ? `ID: ${task.groupId}` : t('taskDetailModal.unspecified'))}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {task.branchName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('taskDetailModal.branch')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <BusinessIcon sx={{ fontSize: '1.2rem', mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="medium">
                        {task.branchName}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!readOnly && onDelete && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteConfirmOpen(true)}
                      sx={{ borderRadius: 1 }}
                    >
                      {t('taskDetailModal.deleteTask')}
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* EKLENECEK YER: Görev Tamamlama Fotoğrafı */}
              {task.completionPhotoUrl && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> {t('taskDetailModal.completionPhoto')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    sx={{
                      width: 100, // Thumbnail genişliği
                      height: 100, // Thumbnail yüksekliği
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                      },
                      mx: 'auto' // Ortalamak için eklendi
                    }}
                    onClick={() => setIsCompletionPhotoFullscreenOpen(true)}
                  >
                    <Box
                      component="img"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'cover' // 'contain' yerine 'cover' daha iyi dolgunluk sağlar
                      }}
                      src={task.completionPhotoUrl}
                      alt={t('taskDetailModal.completionPhoto')}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        console.error(`Tamamlama fotoğrafı yüklenirken hata: ${task.completionPhotoUrl}`);
                        e.currentTarget.src = 'https://via.placeholder.com/100?text=Hata';
                      }}
                    />
                  </Box>
                </Paper>
              )}

              {/* Tekrarlı görev bilgileri - eğer tekrarlı bir görevse */}
              {task.isRecurring && (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  <SectionTitle variant="subtitle1">
                    <AccessTimeIcon /> {t('taskDetailModal.recurringInfo')}
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" gutterBottom>
                  {t('taskDetailModal.tolerance')}: {task.startTolerance || 15} {t('taskDetailModal.minutes')}
                </Typography>
                  
                {task.repetitionTimes && task.repetitionTimes.length > 0 && (
                  <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('taskDetailModal.recurringTimes')}:
                    </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {task.repetitionTimes.map((time: string, index: number) => (
                        <Chip
                          key={index}
                          label={time}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>
                  </>
                )}
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {/* Tam ekran resim görüntüleme modalı */}
      <Dialog
        open={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            height: '90vh',
            m: 2,
            borderRadius: 2,
            position: 'relative'
          }
        }}
      >
        {/* Kapat butonu */}
        <IconButton
          onClick={() => setIsFullscreenOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)'
            },
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Resim görüntüleyici */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            position: 'relative'
          }}
        >
          {taskImages && taskImages.length > 0 && (
            <Box
              component="img"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              src={taskImages[currentImageIndex]}
              alt={`Görev Resmi ${currentImageIndex + 1}`}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                console.error(`Tam ekran resim yüklenirken hata: ${taskImages[currentImageIndex]}`);
                e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Resim+Yüklenemedi';
              }}
            />
          )}

          {/* Sayfa numarası */}
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              py: 0.5,
              borderRadius: 2
            }}
          >
            {currentImageIndex + 1} / {taskImages?.length || 0}
          </Typography>

          {/* Önceki/sonraki butonları */}
          {taskImages && taskImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousImage}
                disabled={currentImageIndex === 0}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Typography variant="h3">←</Typography>
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                disabled={currentImageIndex === (taskImages.length - 1)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Typography variant="h3">→</Typography>
              </IconButton>
            </>
          )}
        </Box>
      </Dialog>

      {/* EKLENDİ: Tam ekran tamamlama fotoğrafı görüntüleme modalı */}
      {task.completionPhotoUrl && (
        <Dialog
          open={isCompletionPhotoFullscreenOpen}
          onClose={() => setIsCompletionPhotoFullscreenOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              height: '90vh',
              m: 2,
              borderRadius: 2,
              position: 'relative'
            }
          }}
        >
          <IconButton
            onClick={() => setIsCompletionPhotoFullscreenOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              },
              zIndex: 10
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box
              component="img"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              src={task.completionPhotoUrl}
              alt={t('taskDetailModal.completionPhoto')}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                console.error(`Tam ekran tamamlama fotoğrafı yüklenirken hata: ${task.completionPhotoUrl}`);
                e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Resim+Yüklenemedi';
              }}
            />
          </Box>
        </Dialog>
      )}

      {/* Silme onay diyaloğu */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t('taskDetailModal.deleteConfirmation.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('taskDetailModal.deleteConfirmation.message', { name: task.name })}
          </Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('taskDetailModal.deleteConfirmation.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ ml: 2 }}>
            {isDeleting ? t('taskDetailModal.deleteConfirmation.deleting') : t('taskDetailModal.deleteConfirmation.confirm')}
          </Button>
        </Box>
      </Dialog>

      {/* Personel değiştirme diyaloğu */}
      <Dialog open={personnelChangeOpen} onClose={() => setPersonnelChangeOpen(false)}>
        <DialogTitle>{t('taskDetailModal.changePersonnelDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('taskDetailModal.changePersonnelDialog.message', { taskName: task.name })}
          </Typography>
          {/* Personel seçimi için basit bir liste kullanıyoruz */}
          <List sx={{ width: '100%', maxHeight: 300, overflow: 'auto' }}>
            {personnel.map((person) => (
              <ListItem
                key={person.id}
                button
                selected={selectedPersonnelId === person.id}
                onClick={() => setSelectedPersonnelId(person.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: selectedPersonnelId === person.id ? 'primary.50' : 'transparent'
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: selectedPersonnelId === person.id ? 'primary.main' : 'grey.400' }}>
                    {person.name?.charAt(0) || 'P'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={person.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={() => setPersonnelChangeOpen(false)}>{t('taskDetailModal.changePersonnelDialog.cancel')}</Button>
          <Button 
            onClick={handlePersonnelChangeSubmit} 
            color="primary" 
            variant="contained"
            disabled={isChangingPersonnel || !selectedPersonnelId || selectedPersonnelId === task.personnelId}
            sx={{ ml: 2 }}
          >
            {isChangingPersonnel ? t('taskDetailModal.changePersonnelDialog.changing') : t('taskDetailModal.changePersonnelDialog.confirm')}
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default TaskDetailModal; 